import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase configuration. Please check your environment variables.');
}

// Create Supabase client with enhanced configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'X-Client-Info': 'clean360-react-app'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Connection status checker
export const checkSupabaseConnection = async () => {
  try {
    // Simple query to test connection
    const { data, error } = await supabase?.from('menu_visibility_settings')?.select('count')?.limit(1);
    return !error;
  } catch (error) {
    console.warn('Supabase connection check failed:', error);
    return false;
  }
};

// Network status utility
export const getNetworkStatus = () => {
  return navigator?.onLine ?? true;
};

// Enhanced error handler
export const handleSupabaseError = (error, context = '') => {
  const errorInfo = {
    message: error?.message || 'Unknown error',
    details: error?.details || error?.stack || 'No additional details',
    hint: error?.hint || '',
    code: error?.code || '',
    context,
    networkOnline: getNetworkStatus(),
    timestamp: new Date()?.toISOString()
  };

  // Log different error types
  if (error?.message?.includes('Failed to fetch')) {
    console.warn(`${context} - Network fetch error:`, errorInfo);
  } else if (error?.code) {
    console.error(`${context} - Supabase error [${error?.code}]:`, errorInfo);
  } else {
    console.error(`${context} - Unexpected error:`, errorInfo);
  }

  return errorInfo;
};

// Retry mechanism for failed requests
export const withRetry = async (operation, maxRetries = 3, delayMs = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      lastError = error;
      
      // Don't retry if network is offline
      if (!getNetworkStatus()) {
        throw error;
      }
      
      // Don't retry on authentication errors
      if (error?.code === '401' || error?.message?.includes('auth')) {
        throw error;
      }
      
      if (attempt < maxRetries) {
        console.warn(`Attempt ${attempt} failed, retrying in ${delayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
      }
    }
  }
  
  throw lastError;
};